/**
 * quickStart.js - Pinia store for the per-page Quick Start tour state.
 *
 * The bitfield lives on ApplicationUser.ESignQuickStartFlags. Each bit
 * corresponds to one tour (Dashboard, Packages, Templates, ...). Bit
 * indices MUST stay in sync with Platform.eSignPortal/Models/QuickStartTour.cs.
 *
 * Usage: drop <QuickStartTour tour-key="packages" .../> into a page. The
 * component talks to this store - pages do not call the store directly.
 */
import { defineStore } from 'pinia';
import axios from 'axios';

// Tour bit indices. The server's QuickStartTour enum is the authority - this
// table is a Vue-side mirror so we don't ship the enum names over the wire on
// every call. Treat as immutable; never renumber existing entries.
export const TOUR_BITS = Object.freeze({
    dashboard:       0,
    packages:        1,
    templates:       2,
    bulksends:       3,
    signingGroups:   4,
    team:            5,
    branding:        6,
    identity:        7,
    connectedApps:   8,
    billing:         9,
    subscription:    10,
    signerPayments:  11,
    bankConnection:  12,
    integrations:    13,
    apiKeys:         14,
    webhooks:        15,
    embeddedSigning: 16
});

// Server enum names matching Platform.eSignPortal/Models/QuickStartTour.cs.
// Used in the POST body so the controller can validate against Enum.IsDefined.
const SERVER_TOUR_NAMES = Object.freeze({
    dashboard:       'Dashboard',
    packages:        'Packages',
    templates:       'Templates',
    bulksends:       'BulkSends',
    signingGroups:   'SigningGroups',
    team:            'Team',
    branding:        'Branding',
    identity:        'Identity',
    connectedApps:   'ConnectedApps',
    billing:         'Billing',
    subscription:    'Subscription',
    signerPayments:  'SignerPayments',
    bankConnection:  'BankConnection',
    integrations:    'Integrations',
    apiKeys:         'ApiKeys',
    webhooks:        'Webhooks',
    embeddedSigning: 'EmbeddedSigning'
});

export const useQuickStartStore = defineStore('quickStart', {
    state: () => ({
        // BigInt would be more correct for a 64-bit field, but we only use up
        // to bit 16 today and Number can represent integers up to 2^53 - 1
        // exactly. Sticking with Number keeps the JSON round-trip simple.
        flags: 0,

        // false until the first GET resolves. Components must wait on `loaded`
        // before deciding to render a dialog - otherwise the tour briefly
        // flashes on every page load before the server says "already dismissed".
        loaded: false,

        // In-flight GET promise, deduped so concurrent page mounts don't fire
        // multiple requests on a fresh session.
        _loadPromise: null
    }),

    getters: {
        // Returns a function (Pinia getter convention for parameterized lookups).
        // Defaults to TRUE before `loaded` so the dialog stays hidden until we
        // actually know whether the user dismissed it.
        isDismissed: (state) => (tourKey) => {
            if (!state.loaded) return true;
            const bit = TOUR_BITS[tourKey];
            if (bit == null) return true; // unknown tour - safer to hide than to spam
            return (state.flags & (1 << bit)) !== 0;
        }
    },

    actions: {
        async load() {
            if (this.loaded) return;
            if (this._loadPromise) return this._loadPromise;

            this._loadPromise = (async () => {
                try {
                    const r = await axios.get('/api/user/quickstart-flags');
                    this.flags = Number(r.data?.flags ?? 0);
                } catch (err) {
                    // If we cannot fetch, treat all tours as dismissed (loaded=true,
                    // flags=fully-set). Better to skip the tour than block the page
                    // or spam dialogs on every visit.
                    console.warn('[QuickStart] flags GET failed - suppressing all tours:', err?.message);
                    this.flags = -1 >>> 0; // all 32 bits set; covers every tour bit
                } finally {
                    this.loaded = true;
                    this._loadPromise = null;
                }
            })();

            return this._loadPromise;
        },

        async dismiss(tourKey) {
            const bit = TOUR_BITS[tourKey];
            if (bit == null) {
                console.warn('[QuickStart] unknown tour key:', tourKey);
                return;
            }
            // Optimistic local set so the dialog hides immediately - the POST is
            // best-effort. If it fails the user sees the tour again next load,
            // which is a benign worst case.
            this.flags |= (1 << bit);
            try {
                await axios.post('/api/user/dismiss-quickstart', {
                    tour: SERVER_TOUR_NAMES[tourKey]
                });
            } catch (err) {
                console.warn('[QuickStart] dismiss POST failed:', err?.message);
            }
        }
    }
});
